import React from 'react';
import { Box, Typography, Container, IconButton, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import LaunchIcon from '@mui/icons-material/Launch';
import GitHubIcon from '@mui/icons-material/GitHub';
import { viewportOnce, stagger, staggerItem } from '../motion';
import { projects, Project } from '../content';
import SectionHeading from './SectionHeading';

const TechChip: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    sx={{
      fontFamily: (t) => t.typography.button.fontFamily,
      fontSize: '0.7rem',
      color: 'text.secondary',
      px: 1,
      py: 0.4,
      borderRadius: 1,
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    {label}
  </Box>
);

const ProjectCard: React.FC<{ project: Project }> = ({ project }) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 3, md: 4 },
      borderRadius: 3,
      overflow: 'hidden',
      border: (t) => `1px solid ${t.palette.divider}`,
      backgroundColor: 'background.paper',
      transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1), border-color 0.4s ease',
      // sheen that sweeps on hover
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: (t) =>
          `radial-gradient(600px circle at var(--mx, 50%) var(--my, 0%), ${t.palette.primary.main}10, transparent 40%)`,
        opacity: 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: 'none',
      },
      '&:hover': {
        transform: 'translateY(-6px)',
        borderColor: (t) => `${t.palette.primary.main}55`,
      },
      '&:hover::before': { opacity: 1 },
    }}
    onMouseMove={(e) => {
      const r = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
      e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography
        sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.75rem', color: 'text.secondary' }}
      >
        {project.year}
      </Typography>
      {project.featured && (
        <Box
          sx={{
            fontFamily: (t) => t.typography.button.fontFamily,
            fontSize: '0.65rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'primary.main',
            px: 1,
            py: 0.3,
            borderRadius: 1,
            border: (t) => `1px solid ${t.palette.primary.main}44`,
          }}
        >
          Featured
        </Box>
      )}
    </Box>

    <Typography variant="h4" component="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
      {project.title}
    </Typography>
    <Typography
      sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.78rem', color: 'primary.main', mb: 2 }}
    >
      {project.blurb}
    </Typography>

    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1 }}>
      {project.description}
    </Typography>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
      {project.tech.map((t) => (
        <TechChip key={t} label={t} />
      ))}
    </Box>

    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
      {project.liveUrl && (
        <Tooltip title="Live demo">
          <IconButton
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label={`${project.title} live demo`}
            sx={{
              color: 'text.secondary',
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 1.5,
              '&:hover': { color: 'primary.main', borderColor: (t) => `${t.palette.primary.main}66` },
            }}
          >
            <LaunchIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      )}
      {project.githubUrl && (
        <Tooltip title="Source code">
          <IconButton
            href={project.githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label={`${project.title} source code`}
            sx={{
              color: 'text.secondary',
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 1.5,
              '&:hover': { color: 'primary.main', borderColor: (t) => `${t.palette.primary.main}66` },
            }}
          >
            <GitHubIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

const Projects: React.FC = () => (
  <Box component="section" id="projects" sx={{ py: { xs: 10, md: 18 }, position: 'relative' }}>
    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
      <SectionHeading
        index="04"
        eyebrow="work"
        title="Selected projects"
        subtitle="A few things I've built. Each one taught me something worth keeping."
      />

      <motion.div
        variants={stagger(0.1)}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {projects.map((project) => (
            <motion.div key={project.title} variants={staggerItem} style={{ height: '100%' }}>
              <ProjectCard project={project} />
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Container>
  </Box>
);

export default Projects;
